import { renderPrintDocument } from "../../core/renderPrintDocument";
import { escapeHtml } from "../../shared/escapeHtml";
import { officialDocumentStyles } from "../../shared/officialDocumentStyles";
import { createDeadweightPrintViewModelV2 } from "./createDeadweightPrintViewModelV2";

const deadweightStylesV2 = `
  ${officialDocumentStyles}

  .deadweight-table td:last-child,
  .deadweight-table td:nth-child(2),
  .deadweight-table td:nth-child(3) {
    white-space: nowrap;
  }
`;

const formatValue = (value, unit = "") => {
  if (value === undefined || value === null || value === "") {
    return "\u2014";
  }

  return `${value}${unit ? ` ${unit}` : ""}`;
};

const isChecked = (current, expected) => (current === expected ? "X" : "");

const renderDeadweightBody = (model) => `
  <main class="official-document">
    <header class="official-header">
      <div class="official-logo-wrap">
        ${
          model.companyLogo
            ? `<img src="${escapeHtml(model.companyLogo)}" alt="Company logo" class="official-logo" />`
            : ""
        }
      </div>
      <div class="official-header-main">
        <h1 class="official-vessel-name">${escapeHtml(model.vesselName)}</h1>
        <h2 class="official-title">${escapeHtml(model.title)}</h2>
      </div>
    </header>

    <hr class="official-rule" />

    <table class="official-meta" aria-label="Document metadata">
      <tbody>
        <tr>
          <td class="official-meta-label">Port:</td>
          <td class="official-meta-value">${escapeHtml(model.portName)}</td>
          <td class="official-meta-label">Voyage No.:</td>
          <td class="official-meta-value">${escapeHtml(model.voyageNumber)}</td>
        </tr>
        <tr>
          <td class="official-meta-label">Date:</td>
          <td class="official-meta-value">${escapeHtml(model.tripDate)}</td>
          <td class="official-meta-label">Charterer:</td>
          <td class="official-meta-value">${escapeHtml(model.charterer)}</td>
        </tr>
        <tr>
          <td class="official-meta-label">Cargo:</td>
          <td class="official-meta-value">${escapeHtml(model.cargoName)}</td>
          <td class="official-meta-label">Zone:</td>
          <td class="official-meta-value">${escapeHtml(model.zone)}</td>
        </tr>
      </tbody>
    </table>

    <hr class="official-meta-separator" />

    <section class="official-section">
      <table class="official-table deadweight-table">
        <tbody>
          <tr><th colspan="2">Allowed Deadweight</th></tr>
          <tr><td>Governing DWT</td><td>${escapeHtml(formatValue(model.governingDWT, "Mt"))}</td></tr>
          <tr><td>Bunker Consumption to Summer/Winter Zone</td><td>${escapeHtml(formatValue(model.bunkerConsumption, "Mt"))}</td></tr>
          <tr><td><strong>Total Allowed DWT</strong></td><td><strong>${escapeHtml(formatValue(model.totalAllowedDWT, "Mt"))}</strong></td></tr>
        </tbody>
      </table>
    </section>

    <section class="official-section">
      <table class="official-table deadweight-table">
        <tbody>
          <tr><th colspan="2">Less weight of bunkers/stores etc.</th></tr>
          <tr><td>Bunkers</td><td>${escapeHtml(formatValue(model.bunkers, "Mt"))}</td></tr>
          <tr><td>Freshwater</td><td>${escapeHtml(formatValue(model.freshwater, "Mt"))}</td></tr>
          <tr><td>Stores</td><td>${escapeHtml(formatValue(model.stores, "Mt"))}</td></tr>
          <tr><td>Ballast</td><td>${escapeHtml(formatValue(model.ballast, "Mt"))}</td></tr>
          <tr><td>Slops</td><td>${escapeHtml(formatValue(model.slops, "Mt"))}</td></tr>
          <tr><td>Loss of DWT due draft restrictions in fixture</td><td>${escapeHtml(formatValue(model.lossOfDWT, "Mt"))}</td></tr>
        </tbody>
      </table>
    </section>

    <section class="official-section">
      <table class="official-table deadweight-table">
        <tbody>
          <tr><th colspan="3">Cargo Availability</th></tr>
          <tr><td colspan="2">DWT available for cargo</td><td>${escapeHtml(formatValue(model.availableDWT, "Mt"))}</td></tr>
          <tr><td colspan="2">Cubic available for cargo</td><td>${escapeHtml(formatValue(model.availableCBM, "m3"))}</td></tr>
          <tr><td>At Density</td><td>${escapeHtml(formatValue(model.density))}</td><td>${escapeHtml(formatValue(model.availableMT, "Mt"))}</td></tr>
          <tr><td colspan="2">Max Quantity acc. to C/P fixture</td><td>${escapeHtml(formatValue(model.maxQuantAccCP, "Mt"))}</td></tr>
          <tr><td colspan="2">Quantity according to Bill of Lading</td><td>${escapeHtml(formatValue(model.quantBillOfLaden, "Mt"))}</td></tr>
        </tbody>
      </table>
    </section>

    <section class="official-section">
      <table class="official-table deadweight-table">
        <tbody>
          <tr><th colspan="3">Deadweight Claimed</th></tr>
          <tr><td>DWT available for cargo</td><td>${isChecked(model.claim, "DWT")}</td><td rowspan="3">${escapeHtml(formatValue(model.claimedDeadweight, "Mt"))}</td></tr>
          <tr><td>Cubic available for cargo</td><td>${isChecked(model.claim, "Cubic")}</td></tr>
          <tr><td>Fixed quantity according to Charterparty</td><td>${isChecked(model.claim, "Fixed")}</td></tr>
        </tbody>
      </table>
    </section>

    <section class="official-section">
      <table class="official-table deadweight-table">
        <tbody>
          <tr><th colspan="3">Requested Cargo</th></tr>
          <tr><td>Cargo requested prior/upon arrival</td><td>${escapeHtml(formatValue(model.requestedCargoMT, "MT"))}</td><td>${escapeHtml(formatValue(model.requestedCargoCub, "m3"))}</td></tr>
          <tr><td>Based on</td><td>${escapeHtml(formatValue(model.basedOnDens, "Density"))}</td><td>${escapeHtml(formatValue(model.basedOnTemp, "(Max) Temp."))}</td></tr>
          <tr><td>As per C/P terms</td><td>${isChecked(model.asPer, "CP")}</td><td></td></tr>
          <tr><td>As per orders</td><td>${isChecked(model.asPer, "orders")}</td><td></td></tr>
          <tr><td>Actual deadweight difference</td><td colspan="2">${escapeHtml(formatValue(model.actualDeadweight, "Mt"))}</td></tr>
        </tbody>
      </table>
    </section>
  </main>
`;

export const renderDeadweightV2 = (input) => {
  const model = createDeadweightPrintViewModelV2(input);

  return renderPrintDocument({
    title: model.title,
    templateId: "deadweight",
    viewModel: model,
    styles: deadweightStylesV2,
    body: renderDeadweightBody(model),
  });
};
